/**
 * Phase-1 proxy: map existing Control Bridge view model + structure into hi-fi personal-layer models.
 * Phase 2 will replace proxy week ranges and sequential edges with kryspace manifest fields.
 */

import type { KryspaceControlBridgeStructure, ControlBridgeStructureNode } from '../../../types/kryspaceControlBridgeStructure';
import type { ControlBridgeQueueItem, ControlBridgeViewModel } from '../deriveViewModel';
import { isCompanyArtifactInstanceId } from '../detail/resolveArtifactInstanceId';
import { scopeIdsEqual } from '../../../utils/scopeIdPlane';
import {
  driveLifecycleFromPhrase,
  isBlockedStatusPhrase,
  isMovedStatusPhrase,
} from '../../../utils/statusPhrasePlane';
import { normalizeResourceRef } from '../resolveUserResourceProfileRef';
import { deriveGuidanceSessions, type GuidanceImprovementRow, type GuidanceWaypointRow } from '../deriveGuidanceModel';
import { deriveDriveSubdomainRows, filterDriveLaneChildNodes } from '../deriveSubdomainFlow';
import { driveOperationalFlowItems } from './operationalFlowFilter';
import { fixUtf8Mojibake } from '../fixUtf8Mojibake';
import {
  deriveGuidanceSummary,
  guidanceLayoutConstants,
  proxyWeekRangeFromIndex,
  rollupCounts,
  sequentialSubdomainEdges,
  statusFromPhrase,
} from './derivation';
import { weekRangeFromQueueItem } from './guidanceSchedule';
import type {
  DriveArtifactItem,
  DriveGrandchildLane,
  DriveHiFiModel,
  DriveSubdomainNode,
  GuidanceHiFiModel,
  GuidanceTreeNode,
} from './types';

export interface PersonalLayerMapContext {
  /** Display name for Me overlay (e.g. logged-in user). */
  currentUserLabel?: string;
  /** Resource ref/id for Me overlay matching on queue items. */
  currentUserRef?: string;
  /** Sidebar scope for Drive subdomain columns. */
  scope?: string;
  /** Scope/resource-filtered queue slices (when omitted, full vm queues are used). */
  eligibleWork?: ControlBridgeQueueItem[];
  openIssues?: ControlBridgeQueueItem[];
}

function queueVm(vm: ControlBridgeViewModel, ctx: PersonalLayerMapContext): ControlBridgeViewModel {
  if (!ctx.eligibleWork && !ctx.openIssues) return vm;
  return {
    ...vm,
    eligibleWork: ctx.eligibleWork ?? vm.eligibleWork,
    openIssues: ctx.openIssues ?? vm.openIssues,
  };
}

function isMine(item: ControlBridgeQueueItem, ctx: PersonalLayerMapContext): boolean {
  const want = normalizeResourceRef(ctx.currentUserRef)?.toLowerCase();
  const assigned = normalizeResourceRef(item.assignedResourceRef)?.toLowerCase();
  if (want && assigned && want === assigned) return true;
  const userLabel = ctx.currentUserLabel ? fixUtf8Mojibake(ctx.currentUserLabel) : '';
  const assignedLabel = item.assignedResourceLabel ? fixUtf8Mojibake(item.assignedResourceLabel) : '';
  if (userLabel && assignedLabel && userLabel === assignedLabel) return true;
  return false;
}

function activitiesForImprovement(
  improvementId: string,
  _waypointId: string,
  eligibleWork: ControlBridgeQueueItem[],
  openIssues: ControlBridgeQueueItem[]
): ControlBridgeQueueItem[] {
  const seen = new Set<string>();
  const out: ControlBridgeQueueItem[] = [];
  for (const item of [...eligibleWork, ...openIssues]) {
    if (!item.itemId || seen.has(item.itemId)) continue;
    if (item.improvementRef !== improvementId) continue;
    seen.add(item.itemId);
    out.push(item);
  }
  return out;
}

function activityNode(
  item: ControlBridgeQueueItem,
  rangeIndex: number,
  ctx: PersonalLayerMapContext,
  opts: { critical?: boolean; leverage?: boolean; blocker?: boolean; dep?: string }
): GuidanceTreeNode {
  const range = weekRangeFromQueueItem(item, rangeIndex);
  const status = statusFromPhrase(item.statusPhrase);
  const pct = status === 'done' ? 1 : status === 'active' ? 0.55 : 0;
  return {
    id: item.itemId,
    type: 'activity',
    label: fixUtf8Mojibake(item.title),
    status,
    statusPhrase: item.statusPhrase,
    range,
    pct,
    res: item.assignedResourceLabel != null ? fixUtf8Mojibake(item.assignedResourceLabel) : undefined,
    dep: opts.dep,
    critical: opts.critical,
    leverage: opts.leverage,
    blocker: opts.blocker,
    mine: isMine(item, ctx),
  };
}

function improvementNode(
  imp: GuidanceImprovementRow,
  waypointId: string,
  rangeIndex: number,
  vm: ControlBridgeViewModel,
  ctx: PersonalLayerMapContext
): GuidanceTreeNode {
  const activities = activitiesForImprovement(imp.itemId, waypointId, vm.eligibleWork, vm.openIssues).filter(
    (item) => item.itemId !== imp.itemId
  );
  const open = imp.openActivities + imp.openIssues;
  const total = Math.max(open, activities.length, 1);
  const range = proxyWeekRangeFromIndex(rangeIndex, 2, 1);
  const status = statusFromPhrase(imp.statusPhrase);
  const childNodes = activities.map((item, i) =>
    activityNode(item, rangeIndex + i + 1, ctx, {
      critical: i === 0 && imp.priority === 'high',
      leverage: /leverage|unlock/i.test(item.title),
      blocker: /block/i.test(item.statusPhrase),
      dep: i > 0 ? activities[i - 1]!.itemId : undefined,
    })
  );
  const hasBlocker = childNodes.some((c) => c.blocker) || imp.openIssues > 0;
  return {
    id: imp.itemId,
    type: 'improvement',
    label: fixUtf8Mojibake(imp.title),
    status,
    statusPhrase: imp.statusPhrase,
    openActivities: imp.openActivities,
    range,
    counts: rollupCounts(open, total),
    plankDescription: imp.plankDescription != null ? fixUtf8Mojibake(imp.plankDescription) : undefined,
    futureCondition: imp.futureCondition != null ? fixUtf8Mojibake(imp.futureCondition) : undefined,
    resourceAssignment: imp.resourceAssignment != null ? fixUtf8Mojibake(imp.resourceAssignment) : undefined,
    assumptionText: imp.assumptionText != null ? fixUtf8Mojibake(imp.assumptionText) : undefined,
    estimatedDuration: imp.estimatedDuration != null ? fixUtf8Mojibake(imp.estimatedDuration) : undefined,
    startDate: imp.startDate,
    endDate: imp.endDate,
    duration: imp.duration,
    blocker: hasBlocker,
    blockerCount: imp.openIssues,
    leverage: childNodes.some((c) => c.leverage),
    mine: childNodes.some((c) => c.mine),
    children: childNodes.length > 0 ? childNodes : undefined,
  };
}

function waypointNode(
  wp: GuidanceWaypointRow,
  rangeIndex: number,
  vm: ControlBridgeViewModel,
  ctx: PersonalLayerMapContext
): GuidanceTreeNode {
  const improvements = wp.improvements.map((imp, i) =>
    improvementNode(imp, wp.waypointId, rangeIndex + i + 1, vm, ctx)
  );
  const range = proxyWeekRangeFromIndex(rangeIndex, 3, 1.5);
  const open = wp.improvementsOpen + wp.activitiesOpen;
  const total = Math.max(wp.improvementsTotal + wp.activitiesTotal, improvements.length, 1);
  return {
    id: wp.waypointId,
    type: 'waypoint',
    label: fixUtf8Mojibake(wp.title),
    status: statusFromPhrase(wp.statusPhrase),
    range,
    artifact: fixUtf8Mojibake(wp.title),
    counts: rollupCounts(open, total),
    critical: wp.sequenceOrder === 1,
    leverage: improvements.some((i) => i.leverage),
    blocker: improvements.some((i) => i.blocker),
    blockerCount: wp.issuesOpen,
    mine: improvements.some((i) => i.mine),
    children: improvements.length > 0 ? improvements : undefined,
  };
}

function sessionRoot(
  sessionLabel: string,
  sessionKey: string,
  waypoints: GuidanceWaypointRow[],
  vm: ControlBridgeViewModel,
  ctx: PersonalLayerMapContext
): GuidanceTreeNode {
  const children = waypoints.map((wp, i) => waypointNode(wp, i + 1, vm, ctx));
  const maxL = children.reduce((m, c) => Math.max(m, c.range.l), 0);
  const range = { s: 0, e: Math.max(1, maxL - 2), l: maxL || 6 };
  return {
    id: sessionKey,
    type: 'session',
    label: fixUtf8Mojibake(sessionLabel),
    status: children.some((c) => c.status === 'active') ? 'active' : 'todo',
    range,
    counts: rollupCounts(
      waypoints.reduce((s, w) => s + w.improvementsOpen + w.activitiesOpen, 0),
      waypoints.reduce((s, w) => s + w.improvementsTotal + w.activitiesTotal, 0)
    ),
    children,
  };
}

export function mapGuidanceHiFiModel(
  vm: ControlBridgeViewModel,
  ctx: PersonalLayerMapContext = {}
): GuidanceHiFiModel | null {
  const scopedVm = queueVm(vm, ctx);
  const sessions = deriveGuidanceSessions(scopedVm);
  const session = sessions[0];
  if (!session || session.waypoints.length === 0) return null;

  const root = sessionRoot(session.sessionLabel, session.sessionKey, session.waypoints, scopedVm, ctx);
  const layout = guidanceLayoutConstants();

  return {
    sessionId: session.sessionKey,
    sessionLabel: fixUtf8Mojibake(session.sessionLabel),
    objectiveLabel: fixUtf8Mojibake(vm.companyTitle),
    root,
    summary: deriveGuidanceSummary(root),
    weekCount: layout.weekCount,
    weekWidthPx: layout.weekWidthPx,
    treeColumnWidthPx: layout.treeColumnWidthPx,
    eligibleWork: scopedVm.eligibleWork,
  };
}

function findStructureNode(
  structure: KryspaceControlBridgeStructure,
  scopeId: string
): ControlBridgeStructureNode | undefined {
  for (const group of structure.groups) {
    for (const node of group.nodes) {
      const nodeScope = node.filterScope ?? node.key;
      if (scopeIdsEqual(nodeScope, scopeId)) return node;
      for (const child of node.children ?? []) {
        const childScope = child.filterScope ?? child.key;
        if (scopeIdsEqual(childScope, scopeId)) return child;
        for (const grand of child.children ?? []) {
          const grandScope = grand.filterScope ?? grand.key;
          if (scopeIdsEqual(grandScope, scopeId)) return grand;
        }
      }
    }
  }
  return undefined;
}

function grandchildLanesForScope(
  structure: KryspaceControlBridgeStructure,
  scopeId: string,
  openIssues: ControlBridgeQueueItem[],
  eligibleWork: ControlBridgeQueueItem[]
): DriveGrandchildLane[] | undefined {
  const node = findStructureNode(structure, scopeId);
  if (!node?.children?.length) return undefined;
  const kids = filterDriveLaneChildNodes(node.children, openIssues, eligibleWork);
  if (!kids.length) return undefined;
  return kids.map((child) => ({
    id: child.filterScope ?? child.key,
    name: fixUtf8Mojibake(child.label),
  }));
}

function resolveArtifactPlacement(
  item: ControlBridgeQueueItem,
  subdomainId: string,
  grandchildren?: DriveGrandchildLane[]
): { subdomainId: string; lane?: string } {
  const itemScope = item.filterScope ?? item.domain;
  if (grandchildren?.length && itemScope) {
    const lane = grandchildren.find((g) => scopeIdsEqual(g.id, itemScope));
    if (lane) return { subdomainId, lane: lane.id };
  }
  return { subdomainId };
}

function mapArtifact(
  item: ControlBridgeQueueItem,
  ctx: PersonalLayerMapContext,
  subdomainId: string,
  nextSubdomainId?: string,
  lane?: string
): DriveArtifactItem {
  const blocked = isBlockedStatusPhrase(item.statusPhrase);
  const moved = isMovedStatusPhrase(item.statusPhrase);
  const life = driveLifecycleFromPhrase(item.statusPhrase);
  const expected = life === 'active' && !blocked && !moved;
  const scopeRef = (item.filterScope ?? item.domain ?? '').trim();
  const companyArtifactRef =
    scopeRef && isCompanyArtifactInstanceId(scopeRef) ? scopeRef : undefined;
  return {
    id: item.itemId,
    title: fixUtf8Mojibake(item.title),
    sub: fixUtf8Mojibake(item.improvementTitle ?? item.waypointRef ?? item.filterScope ?? ''),
    subdomainId,
    filterScope: item.filterScope,
    companyArtifactRef,
    life,
    owner: item.assignedResourceLabel != null ? fixUtf8Mojibake(item.assignedResourceLabel) : 'Unassigned',
    status: blocked ? 'blocked' : moved ? 'moved' : expected ? 'expected' : 'normal',
    cause: blocked ? fixUtf8Mojibake(item.statusPhrase) : undefined,
    movedAgo: moved ? '2h' : undefined,
    expTo: expected ? nextSubdomainId : undefined,
    expWhen: expected ? '~1d' : undefined,
    projectedTo: expected ? nextSubdomainId : moved ? nextSubdomainId : undefined,
    mine: isMine(item, ctx),
    lane,
    statusDate: item.statusDate,
  };
}

export function mapDriveHiFiModel(
  structure: KryspaceControlBridgeStructure,
  vm: ControlBridgeViewModel,
  ctx: PersonalLayerMapContext = {}
): DriveHiFiModel {
  const scope = ctx.scope ?? 'company';
  const openIssues = ctx.openIssues ?? vm.openIssues;
  const eligibleWork = ctx.eligibleWork ?? vm.eligibleWork;
  const rows = deriveDriveSubdomainRows(structure, scope, openIssues, eligibleWork);

  const subdomains: DriveSubdomainNode[] = rows.map((row) => {
    const grandchildren = grandchildLanesForScope(
      structure,
      row.scopeId,
      openIssues,
      eligibleWork
    );
    const grandEdges =
      grandchildren && grandchildren.length > 1
        ? sequentialSubdomainEdges(grandchildren.map((lane) => lane.id))
        : undefined;
    return {
      id: row.scopeId,
      name: fixUtf8Mojibake(row.label),
      grandchildren,
      grandEdges,
    };
  });

  const edges = sequentialSubdomainEdges(subdomains.map((s) => s.id));
  const subdomainOrder = subdomains.map((s) => s.id);
  const nextSubdomain = (id: string) => {
    const idx = subdomainOrder.indexOf(id);
    return idx >= 0 && idx < subdomainOrder.length - 1 ? subdomainOrder[idx + 1] : undefined;
  };

  const artifacts: DriveArtifactItem[] = [];
  for (const row of rows) {
    const subdomain = subdomains.find((s) => s.id === row.scopeId);
    const flowItems = driveOperationalFlowItems([...row.activities, ...row.issues]);
    for (const item of flowItems) {
      const placement = resolveArtifactPlacement(item, row.scopeId, subdomain?.grandchildren);
      artifacts.push(
        mapArtifact(
          item,
          ctx,
          placement.subdomainId,
          nextSubdomain(row.scopeId),
          placement.lane
        )
      );
    }
  }

  const movedRecently = artifacts.filter((a) => a.status === 'moved').length;
  const blocked = artifacts.filter((a) => a.status === 'blocked').length;

  return {
    domainLabel: fixUtf8Mojibake(structure.companyTitle),
    subdomains,
    edges,
    artifacts,
    summary: {
      inFlow: artifacts.length,
      movedRecently,
      blocked,
      expectedToMove: artifacts.filter((a) => a.life === 'active').length,
    },
  };
}

export const PERSONAL_LAYER_SPEC_VERSION = '2026-06-26';
